import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Sparkles,
  Crown,
  Copy,
  RefreshCcw,
  Filter,
  Wand2,
} from "lucide-react";
import Layout from "../../components/Layout";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";

const REASONS = [
  { value: "refund", label: "Remboursement" },
  { value: "gift", label: "Cadeau" },
  { value: "promo", label: "Promo / Marketing" },
  { value: "internal_qa", label: "QA interne" },
  { value: "support", label: "Support client" },
  { value: "other", label: "Autre" },
];

export default function AdminCodes() {
  const navigate = useNavigate();
  // Generator form
  const [count, setCount] = useState(1);
  const [codeType, setCodeType] = useState("standard");
  const [reason, setReason] = useState("gift");
  const [reasonNote, setReasonNote] = useState("");
  const [beneficiaryEmail, setBeneficiaryEmail] = useState("");
  const [generating, setGenerating] = useState(false);
  const [lastMinted, setLastMinted] = useState(null);

  // Ledger
  const [filterType, setFilterType] = useState("");
  const [filterOrigin, setFilterOrigin] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const loadLedger = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType) params.set("code_type", filterType);
      if (filterOrigin) params.set("origin", filterOrigin);
      if (filterStatus) params.set("status", filterStatus);
      if (search.trim()) params.set("q", search.trim());
      params.set("limit", "100");
      const { data } = await api.get(`/admin/codes?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLedger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, filterOrigin, filterStatus]);

  const generate = async () => {
    const n = Number(count);
    if (!n || n < 1 || n > 200) {
      toast.error("Choisis un nombre entre 1 et 200");
      return;
    }
    setGenerating(true);
    setLastMinted(null);
    try {
      const { data } = await api.post("/admin/codes/generate", {
        count: n,
        code_type: codeType,
        reason,
        reason_note: reasonNote.trim() || undefined,
        beneficiary_email: beneficiaryEmail.trim().toLowerCase() || undefined,
      });
      setLastMinted(data);
      toast.success(`${data.count} code(s) ${codeType} générés`);
      loadLedger();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGenerating(false);
    }
  };

  const copy = async (txt) => {
    try {
      await navigator.clipboard.writeText(txt);
      toast.success("Copié");
    } catch {
      toast.error("Impossible de copier");
    }
  };

  const exportCsv = () => {
    if (items.length === 0) return;
    const header = [
      "code",
      "code_type",
      "origin",
      "status",
      "amount_paid",
      "user_id",
      "payer_email",
      "beneficiary_email",
      "admin_reason",
      "created_at",
    ];
    const rows = items.map((i) =>
      header
        .map((h) => {
          const v = i[h];
          if (v == null) return "";
          const s = String(v).replace(/"/g, '""');
          return `"${s}"`;
        })
        .join(","),
    );
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `otra-codes-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
        <button
          onClick={() => navigate("/admin")}
          data-testid="admin-codes-back"
          className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour console
        </button>

        <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight mb-2">
          Codes
        </h1>
        <p className="text-white/60 text-sm mb-8">
          Génère des codes manuels (remboursement, cadeau, QA) et consulte
          l&apos;historique global.
        </p>

        {/* Generator */}
        <div className="panel rounded-2xl p-5 sm:p-6 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Wand2 className="w-5 h-5 text-cyan-300" />
            <h2 className="font-display font-bold uppercase tracking-tight text-lg">
              Générer des codes
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-3 mb-3">
            <button
              type="button"
              data-testid="admin-codes-type-standard"
              onClick={() => setCodeType("standard")}
              className={`p-3 rounded-xl border text-left transition-all ${
                codeType === "standard"
                  ? "border-cyan-400 bg-cyan-400/10"
                  : "border-white/10 hover:border-white/30"
              }`}
            >
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-cyan-300" />
                <span className="font-display font-bold">Code standard</span>
              </div>
              <p className="text-xs text-white/55 mt-1">
                Utilisable sur les défis standards.
              </p>
            </button>
            <button
              type="button"
              data-testid="admin-codes-type-golden"
              onClick={() => setCodeType("golden")}
              className={`p-3 rounded-xl border text-left transition-all ${
                codeType === "golden"
                  ? "border-amber-400 bg-amber-400/10 shadow-[0_0_25px_rgba(251,191,36,0.25)]"
                  : "border-white/10 hover:border-white/30"
              }`}
            >
              <div className="flex items-center gap-2">
                <Crown className="w-4 h-4 text-amber-300" />
                <span className="font-display font-bold text-amber-200">Golden Code</span>
              </div>
              <p className="text-xs text-white/55 mt-1">
                Accès aux Golden Quizz exclusifs.
              </p>
            </button>
          </div>

          <div className="grid sm:grid-cols-3 gap-3 mb-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-white/60 font-mono-stat">
                Quantité
              </span>
              <input
                data-testid="admin-codes-count"
                type="number"
                min={1}
                max={200}
                value={count}
                onChange={(e) => setCount(e.target.value)}
                className="mt-1 w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white font-mono-stat"
              />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-[10px] uppercase tracking-widest text-white/60 font-mono-stat">
                Raison
              </span>
              <select
                data-testid="admin-codes-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="mt-1 w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white"
              >
                {REASONS.map((r) => (
                  <option key={r.value} value={r.value} className="bg-[#0a0a16]">
                    {r.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mb-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-white/60 font-mono-stat">
                Email bénéficiaire (optionnel)
              </span>
              <input
                data-testid="admin-codes-beneficiary"
                type="email"
                placeholder="player@email.com"
                value={beneficiaryEmail}
                onChange={(e) => setBeneficiaryEmail(e.target.value)}
                className="mt-1 w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white"
              />
              <span className="block text-[10px] text-white/40 mt-1">
                Si l&apos;email correspond à un compte existant, les codes sont automatiquement
                liés à <em>Mes codes</em> de ce joueur.
              </span>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-white/60 font-mono-stat">
                Note (optionnel)
              </span>
              <input
                data-testid="admin-codes-note"
                type="text"
                placeholder="Ticket #1234, geste commercial…"
                value={reasonNote}
                onChange={(e) => setReasonNote(e.target.value)}
                maxLength={240}
                className="mt-1 w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white"
              />
            </label>
          </div>
          <button
            onClick={generate}
            disabled={generating}
            data-testid="admin-codes-generate"
            className={`w-full sm:w-auto px-5 py-3 rounded-xl font-display font-bold uppercase tracking-widest text-sm active:scale-95 transition-all disabled:opacity-60 ${
              codeType === "golden"
                ? "bg-amber-400 hover:bg-amber-300 text-black"
                : "bg-cyan-400 hover:bg-cyan-300 text-black"
            }`}
          >
            {generating ? "Génération…" : `Générer ${count || 1} code(s)`}
          </button>

          {lastMinted && (
            <div
              data-testid="admin-codes-last-batch"
              className="mt-5 p-4 rounded-xl bg-black/40 border border-cyan-400/30"
            >
              <div className="text-xs uppercase tracking-widest text-cyan-300 mb-2">
                Dernier lot ({lastMinted.count} codes)
              </div>
              <div className="flex flex-wrap gap-2">
                {lastMinted.codes.map((c) => (
                  <button
                    key={c.code}
                    onClick={() => copy(c.code)}
                    className={`group inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border ${
                      c.code_type === "golden"
                        ? "border-amber-400/50 bg-amber-400/10 text-amber-200"
                        : "border-cyan-400/30 bg-cyan-400/5 text-cyan-200"
                    }`}
                  >
                    <span className="font-mono-stat tracking-widest">{c.code}</span>
                    <Copy className="w-3 h-3 opacity-50 group-hover:opacity-100" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Ledger */}
        <div className="panel rounded-2xl p-5 sm:p-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-fuchsia-300" />
            <h2 className="font-display font-bold uppercase tracking-tight text-lg flex-1">
              Historique des codes
            </h2>
            <span className="text-xs text-white/50 font-mono-stat">{total} au total</span>
          </div>

          <div className="grid sm:grid-cols-4 gap-2 mb-4">
            <select
              data-testid="admin-codes-filter-type"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
            >
              <option value="">Type : tous</option>
              <option value="standard">Standard</option>
              <option value="golden">Golden</option>
            </select>
            <select
              data-testid="admin-codes-filter-origin"
              value={filterOrigin}
              onChange={(e) => setFilterOrigin(e.target.value)}
              className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
            >
              <option value="">Origine : toutes</option>
              <option value="purchase">Achat Stripe</option>
              <option value="admin">Manuel admin</option>
            </select>
            <select
              data-testid="admin-codes-filter-status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
            >
              <option value="">Statut : tous</option>
              <option value="available">Disponible</option>
              <option value="reserved">Réservé</option>
              <option value="consumed">Consommé</option>
              <option value="completed">Terminé</option>
              <option value="no_show">No-show</option>
            </select>
            <input
              data-testid="admin-codes-search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") loadLedger();
              }}
              placeholder="code, email, user_id…"
              className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
            />
          </div>
          <div className="flex items-center gap-2 mb-4">
            <button
              data-testid="admin-codes-reload"
              onClick={loadLedger}
              disabled={loading}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm font-display uppercase tracking-widest disabled:opacity-50"
            >
              <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Actualiser
            </button>
            <button
              data-testid="admin-codes-export"
              onClick={exportCsv}
              disabled={items.length === 0}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm font-display uppercase tracking-widest disabled:opacity-50"
            >
              Export CSV
            </button>
          </div>

          {items.length === 0 ? (
            <div className="text-center text-white/40 py-10 text-sm">
              Aucun code ne correspond à ces filtres.
            </div>
          ) : (
            <div className="overflow-x-auto -mx-5 sm:mx-0">
              <table className="w-full text-xs sm:text-sm">
                <thead>
                  <tr className="text-left text-white/40 uppercase tracking-widest font-mono-stat text-[10px]">
                    <th className="px-3 py-2">Code</th>
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2">Origine</th>
                    <th className="px-3 py-2">Statut</th>
                    <th className="px-3 py-2 hidden sm:table-cell">Bénéficiaire</th>
                    <th className="px-3 py-2 hidden md:table-cell">Raison</th>
                    <th className="px-3 py-2 hidden lg:table-cell">Créé le</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr
                      key={it.id}
                      data-testid={`admin-codes-row-${it.code}`}
                      className="border-t border-white/5 hover:bg-white/5"
                    >
                      <td className="px-3 py-2.5 font-mono-stat font-bold tracking-widest">
                        <button
                          onClick={() => copy(it.code)}
                          className="text-white hover:text-cyan-300 transition-colors"
                          title="Copier"
                        >
                          {it.code}
                        </button>
                      </td>
                      <td className="px-3 py-2.5">
                        {it.code_type === "golden" ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-amber-400/50 bg-amber-400/10 text-amber-200 text-[10px] uppercase tracking-widest font-display font-bold">
                            <Crown className="w-2.5 h-2.5" />
                            Golden
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-cyan-400/30 text-cyan-200 text-[10px] uppercase tracking-widest font-mono-stat">
                            Standard
                          </span>
                        )}
                        {it.is_bonus && (
                          <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full bg-fuchsia-400/15 text-fuchsia-200 text-[9px] uppercase tracking-widest">
                            Bonus
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-white/65 text-xs">
                        {it.origin === "admin" ? "Manuel" : "Achat"}
                        {it.pack_type && it.pack_type !== "single" && (
                          <span className="block text-[10px] text-white/40 font-mono-stat">
                            {it.pack_type}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="text-white/75 font-mono-stat text-[11px] uppercase tracking-widest">
                          {it.status}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 hidden sm:table-cell text-white/60 text-xs truncate max-w-[200px]">
                        {it.beneficiary_email || it.payer_email || (it.user_id ? "user:" + it.user_id.slice(-6) : "—")}
                      </td>
                      <td className="px-3 py-2.5 hidden md:table-cell text-white/55 text-xs truncate max-w-[200px]">
                        {it.admin_reason
                          ? `${it.admin_reason}${it.admin_reason_note ? " — " + it.admin_reason_note : ""}`
                          : "—"}
                      </td>
                      <td className="px-3 py-2.5 hidden lg:table-cell text-white/40 text-[11px] font-mono-stat">
                        {it.created_at
                          ? new Date(it.created_at).toLocaleString("fr-FR", {
                              day: "2-digit",
                              month: "short",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
